'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import cn from 'classnames';
import { type Icon, type IconProps } from '@phosphor-icons/react';

import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import TextButton from '_components/UI/TextButton';
import IconButton, { type IconButtonProps } from '_components/UI/IconButton';
import LoadingSpinner from '@components/ui/LoadingSpinner';
// Relative, not `@components/...` — the alias resolves before Jest's
// blanket `.svg` mock rule in `moduleNameMapper`, so an aliased import here
// hits the real file (and its non-JS content) under test instead of the mock.
import CartAddSvg from '../../../components/ui/Icons/cart-add.svg';

import styles from './QuickAddTrigger.module.scss';

/**
 * Adapts the shared `LoadingSpinner` (a CSS spinner, not an SVG icon) to
 * Phosphor's `Icon` shape so it can stand in for `IconButton`'s `icon` prop
 * while an add is in flight (`pending` below). `currentColor` picks up
 * whatever the button face's own icon colour already is — white on the
 * primary/inline face — instead of needing its own colour prop threaded in.
 */
const SpinnerIcon: Icon = forwardRef<SVGSVGElement, IconProps>(
  (_props, _ref) => <LoadingSpinner colour="currentColor" />
);
SpinnerIcon.displayName = 'SpinnerIcon';

/**
 * Adapts `components/ui/Icons/cart-add.svg` — the cart+plus glyph exported
 * and combined from Figma 801:82864's "ShoppingCart" + "Frame" (plus) layers,
 * at their exact relative position (see the Figma metadata for that node) —
 * to Phosphor's `Icon` shape so it drops straight into `IconButton`'s `icon`
 * prop (and, via `className`, `TextButton`'s `leadingIcon`). Used by all
 * three faces now — the two icon faces plus the desktop hover-CTA — so
 * the same glyph reads as "add to cart" everywhere Quick Add appears,
 * instead of the CTA alone showing Phosphor's plain, badge-less
 * `ShoppingCartIcon`. The two source layers' strokes were flattened and
 * unioned into one filled path (see the SVG's own `d`); the source weights
 * (1.25/1.5 of 24 units) were also bumped ~40% before flattening; at this
 * icon's actual 16-24px render size, Figma's exact weight came out under 1
 * device px and anti-aliased into a soft line no matter the format, so the
 * fill vs. stroke change alone didn't fix the softness the way flattening
 * usually does. That single path uses `fill="currentColor"`, so this needs
 * no colour prop — it picks up whatever the host button's own per-face CSS
 * (`.primary svg { color }` / `.iconButton.iconButton svg { color }` /
 * `TextButton`'s `.leadingIcon`) already applies, the same as the plain
 * cart glyph did before this badge existed.
 *
 * `viewBox` is passed explicitly (not left to the source file) because
 * SVGO's `removeViewBox` optimization — on by default in both the app's own
 * `@svgr/webpack` pipeline and Storybook's — strips a `viewBox` that exactly
 * matches the source's own `width`/`height`. Without it reasserted here, a
 * smaller `size` just clips the 24×24 artwork instead of scaling it down.
 * `className` is forwarded (unlike `weight`, which this fixed single-path
 * glyph has no use for) since `TextButton` relies on it for its own
 * `.leadingIcon` layout styling — `IconButton` never passes one, so this
 * was never exercised before the CTA started using this icon too.
 */
const CartAddIcon: Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ size, className }, _ref) => (
    <CartAddSvg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
    />
  )
);
CartAddIcon.displayName = 'CartAddIcon';

export interface QuickAddTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visible CTA label (desktop face only). */
  label: string;
  /** Compact always-visible icon face (mobile); default is the hover CTA face. */
  isMobileLayout?: boolean;
  /**
   * Icon face styling, only meaningful when `isMobileLayout` is true:
   * - `'overlay'` (default): white/outline, pinned to the bottom-right
   *   corner of an image-sized host (PLP grid card).
   * - `'inline'`: primary/filled, sized to fill its host rather than
   *   corner-pinned — for a host that IS the button's own slot, not an
   *   image (`ProductTile`'s hotspot card, Figma node 427:7198).
   */
  iconVariant?: 'overlay' | 'inline';
  /**
   * Swaps the cart icon for a spinner and stops the button from reopening
   * the popover — `ImageHotspot`'s compact/stacked Quick Add closes its
   * size-selector card entirely while an add is in flight (Figma 867:163554)
   * rather than showing a spinner inside it, so the button itself is what
   * tells the shopper something's happening. Not native `disabled`: that
   * would also fade the button (`IconButton`'s `.disabled`), which reads as
   * "can't be used" rather than "in progress" — the spinner already says
   * that, so only the click handler is suppressed and `aria-disabled` is set
   * instead, keeping the button's normal appearance and focusability.
   */
  pending?: boolean;
  'aria-label': string;
}

/**
 * Quick Add trigger rendered over the PLP card image. Desktop: white
 * `TextButton` CTA (Figma "Text Button") revealed on card hover/focus.
 * Mobile: always-visible `IconButton` in the image's bottom-right corner.
 * Popover props (onClick, aria-*, data-popup-open, ref) pass through, so it
 * can be used with Base UI `Popover.Trigger`'s `render` prop.
 */
const QuickAddTrigger = forwardRef<HTMLButtonElement, QuickAddTriggerProps>(
  (
    {
      label,
      isMobileLayout = false,
      iconVariant = 'overlay',
      pending = false,
      className,
      'aria-label': ariaLabel,
      onClick,
      ...rest
    },
    ref
  ) => {
    // Only the `inline` face (ImageHotspot's `MiniProductCard` cart action,
    // Figma 419:6774) has its own Mobile/Desktop size variants — 44px/20px
    // icon below `tablet-xl` (988px, matching `MiniProductCard`'s own image
    // breakpoint), 56px/24px icon above it. The `overlay` face (PLP grid,
    // `isMobileLayout` already true only on real mobile) has no such split,
    // so this is read but unused there.
    const isCompactInline = useBreakpoint(Breakpoints.tabletXl);

    if (isMobileLayout) {
      const inline = iconVariant === 'inline';
      return (
        <IconButton
          {...rest}
          ref={ref}
          // IconButton's handler is typed for button|anchor; ours is button-only.
          // Suppressed entirely while pending — see the prop's own doc for why.
          onClick={
            pending ? undefined : (onClick as IconButtonProps['onClick'])
          }
          // Figma 801:82864's own cart+plus glyph (see `CartAddIcon` above)
          // — a single icon, not a cart icon with a badge overlaid on top.
          icon={pending ? SpinnerIcon : CartAddIcon}
          type={inline ? 'primary' : 'outline'}
          size={inline ? (isCompactInline ? 'md' : 'xl') : 'sm'}
          shape="square"
          iconSize={inline ? (isCompactInline ? 20 : 24) : 16}
          aria-label={ariaLabel}
          aria-disabled={pending || undefined}
          className={cn(
            inline ? styles.iconButtonInline : styles.iconButton,
            className
          )}
        />
      );
    }

    return (
      <TextButton
        {...rest}
        ref={ref}
        onClick={onClick}
        type="white"
        size="small"
        label={label}
        leadingIcon={CartAddIcon}
        leadingIconSize={24}
        aria-label={ariaLabel}
        className={cn(styles.cta, className)}
      />
    );
  }
);

QuickAddTrigger.displayName = 'QuickAddTrigger';

export default QuickAddTrigger;
